/**
 * ORDERS WITH THIS VENDOR - the acknowledged-PO panel on Master Data.
 *
 * Asked for 12-09-2026, the twin of "Where is this material?" one tab across.
 * Next to every vendor in the list sits a small button carrying the number of
 * purchase orders that vendor has ACKNOWLEDGED and not yet delivered - the
 * live workload with that supplier. Pressing it opens a panel (on the right on
 * a desktop, sliding up from the bottom on a phone or tablet) listing those
 * orders: number, site, order date, delivery-by date and value, with anything
 * past its delivery date marked. Tapping one opens the purchase order.
 *
 * WHO: Head of Stores, Head of Procurement, Project Head, CMD, CXO, the
 * System Administrator and the Procurement Managers who chase the vendors
 * (CAN.viewVendorOrders). Every other role sees the vendor list exactly as it
 * was, with no button and no query.
 *
 * READ-ONLY and additive: ONE select on purchase_orders, through row level
 * security, loaded once when Master Data opens and re-read whenever a panel is
 * opened. No database change.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, CalendarClock, ClipboardCheck, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'

// deno-lint-ignore no-explicit-any
type Any = any

/**
 * "Vendor acknowledged" and nothing else. An order the vendor has confirmed is
 * a promise outstanding; one still awaiting his confirmation, or already
 * closed, is not what this count is for.
 */
const ACKNOWLEDGED = 'VENDACK'

export interface AckOrder {
  id: string
  vendor_id: string
  site_id: string
  site_name: string
  ordered_on: string | null
  deliver_by: string | null
  value: number
  acknowledged_at: string | null
  overdue: boolean
}

interface Index {
  byVendor: Map<string, AckOrder[]>
  loadedAt: number
}

/** Today in the local calendar, as YYYY-MM-DD - the app works in IST. */
function todayIso(): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/** Every row, a page at a time: the API returns at most 1,000 per call. */
async function all(build: () => Any): Promise<{ data: Any[]; error: { message: string } | null }> {
  const out: Any[] = []
  for (let from = 0; ; from += 1000) {
    const { data, error } = await build().range(from, from + 999)
    if (error) return { data: out, error }
    out.push(...(data ?? []))
    if (!data || data.length < 1000) return { data: out, error: null }
  }
}

async function loadIndex(siteNames: Map<string, string>): Promise<Index> {
  const { data, error } = await all(() => supabase.from('purchase_orders')
    .select('id, vendor_id, site_id, date_created, delivery_date, total_amount, vendor_ack_at')
    .eq('status', ACKNOWLEDGED)
    .order('delivery_date', { ascending: true, nullsFirst: false }))
  if (error) throw new Error(error.message)

  const today = todayIso()
  const byVendor = new Map<string, AckOrder[]>()
  for (const p of (data ?? []) as Any[]) {
    if (!p.vendor_id) continue
    const row: AckOrder = {
      id: p.id,
      vendor_id: p.vendor_id,
      site_id: p.site_id,
      site_name: siteNames.get(p.site_id) ?? p.site_id,
      ordered_on: p.date_created ?? null,
      deliver_by: p.delivery_date ?? null,
      value: Number(p.total_amount) || 0,
      acknowledged_at: p.vendor_ack_at ?? null,
      overdue: !!p.delivery_date && p.delivery_date < today,
    }
    const list = byVendor.get(row.vendor_id)
    if (list) list.push(row); else byVendor.set(row.vendor_id, [row])
  }
  return { byVendor, loadedAt: Date.now() }
}

/**
 * Every acknowledged order in the company, indexed by vendor and loaded once
 * for the Master Data screen. `enabled` is false for every role without the
 * capability, so nobody else pays for the query.
 */
export function useVendorAckOrders(enabled: boolean, sites: { id: string; site_name: string }[]) {
  const [index, setIndex] = useState<Index | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const siteNames = useMemo(() => new Map(sites.map((s) => [s.id, s.site_name])), [sites])

  const refresh = useCallback(async () => {
    if (!enabled) return
    setLoading(true); setError(null)
    try { setIndex(await loadIndex(siteNames)) }
    catch (e) { setError(e instanceof Error ? e.message : String(e)) }
    finally { setLoading(false) }
  }, [enabled, siteNames])

  useEffect(() => { void refresh() }, [refresh])

  // Soonest delivery first; an order with no date promised last.
  const ordersFor = useCallback((vendorId: string): AckOrder[] =>
    [...(index?.byVendor.get(vendorId) ?? [])].sort((a, b) =>
      (a.deliver_by ?? '9999-12-31').localeCompare(b.deliver_by ?? '9999-12-31')
      || b.value - a.value || a.id.localeCompare(b.id)),
  [index])

  const countFor = useCallback((vendorId: string) => index?.byVendor.get(vendorId)?.length ?? 0, [index])

  const overdueFor = useCallback((vendorId: string) =>
    (index?.byVendor.get(vendorId) ?? []).filter((o) => o.overdue).length, [index])

  return { loading, error, refresh, ordersFor, countFor, overdueFor, ready: !!index }
}

/** The small button that sits next to the pencil / eye on each vendor row. */
export function VendorOrdersButton({ count, overdue, active, ready, onClick }: {
  count: number; overdue: number; active: boolean; ready: boolean; onClick: () => void
}) {
  const none = ready && count === 0
  const label = count === 1 ? 'PO' : 'POs'
  return (
    <button
      type="button"
      onClick={onClick}
      // Deliberately the same shape as the material button one tab across, in
      // a different colour: they answer different questions and must not be
      // read as the same control. Quiet at rest, solid while its panel is open.
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border
        px-2.5 py-1 text-[11px] font-semibold leading-none transition-colors duration-150
        focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50 focus-visible:ring-offset-1
        ${active
          ? 'border-transparent bg-gradient-to-r from-teal-700 to-emerald-700 text-white shadow-sm'
          : none
            ? 'border-slate-200 bg-white text-slate-400 hover:border-slate-300 hover:bg-slate-50'
            : 'border-teal-200/80 bg-gradient-to-r from-teal-50 to-emerald-50 text-teal-800 shadow-sm hover:border-teal-300 hover:from-teal-100 hover:to-emerald-100'}`}
      title={none ? 'No order with this vendor is awaiting delivery'
                  : `${count} acknowledged purchase order${count === 1 ? '' : 's'} awaiting delivery`
                    + (overdue > 0 ? ` — ${overdue} past the promised date` : '')}
      aria-label={`Orders awaiting delivery — ${ready ? count : 'loading'}`}
      aria-pressed={active}
    >
      <ClipboardCheck className={`h-3.5 w-3.5 ${active ? 'text-white' : none ? 'text-slate-400' : 'text-teal-600'}`} />
      {!ready ? '…' : (
        <>
          <span className={`rounded-full px-1 tabular-nums ${
            active ? 'bg-white/20' : none ? 'text-slate-400' : 'bg-white/80 text-teal-900'}`}>{count}</span>
          <span className={active ? 'text-white/90' : none ? '' : 'text-teal-700'}>{label}</span>
          {/* One amber dot is enough to say "something here is late" without
              turning a 119-row list into a wall of warnings. */}
          {overdue > 0 && (
            <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-amber-300' : 'bg-amber-500'}`} aria-hidden />
          )}
        </>
      )}
    </button>
  )
}

/**
 * The panel itself. Fixed to the right on large screens (the list beside it
 * stays usable - click another vendor and the panel follows), and a bottom
 * sheet with a backdrop on phones and tablets.
 */
export function VendorOrdersPanel({ vendor, orders, loading, onClose, onRefresh }: {
  vendor: Any
  orders: AckOrder[]
  loading: boolean
  onClose: () => void
  onRefresh: () => void
}) {
  const navigate = useNavigate()

  // Fresh figures every time a vendor is opened.
  useEffect(() => { onRefresh() }, [vendor?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Esc closes, on every form factor.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const t = useMemo(() => ({
    value: orders.reduce((a, o) => a + o.value, 0),
    overdue: orders.filter((o) => o.overdue).length,
    sites: new Set(orders.map((o) => o.site_id)).size,
    next: orders.map((o) => o.deliver_by).filter(Boolean)[0] ?? null,
  }), [orders])

  const open = (poId: string) => { onClose(); navigate(`/pos/${poId}`) }

  return (
    <>
      {/* Backdrop only below the desktop breakpoint - on a desktop the list
          beside the panel stays clickable. */}
      <div className="fixed inset-0 z-40 bg-slate-900/30 lg:hidden" onClick={onClose} aria-hidden />

      <aside
        role="dialog" aria-label={`Orders awaiting delivery from ${vendor?.company_name}`}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[78vh] flex-col rounded-t-2xl bg-white shadow-2xl
                   lg:inset-x-auto lg:bottom-0 lg:right-0 lg:top-0 lg:max-h-none lg:w-[400px] xl:w-[440px]
                   lg:rounded-none lg:border-l lg:border-slate-200"
      >
        <div className="mx-auto mt-2 h-1.5 w-12 rounded-full bg-slate-300 lg:hidden" />

        <header className="flex items-start gap-3 border-b border-slate-100 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-700">Awaiting delivery</p>
            <h2 className="truncate text-base font-bold text-slate-900">{vendor?.company_name}</h2>
            <p className="font-mono text-xs text-slate-500">
              {vendor?.id}{vendor?.gstin ? ` · ${vendor.gstin}` : ''}
            </p>
          </div>
          <button className="btn-ghost btn-sm" onClick={onClose} title="Close (Esc)"><X className="h-5 w-5" /></button>
        </header>

        <div className="grid grid-cols-2 gap-2 border-b border-slate-100 px-4 py-3">
          <Tile label="Orders" value={String(orders.length)} sub={`${t.sites} site${t.sites === 1 ? '' : 's'}`} />
          <Tile label="Order value" value={rupees(t.value)} />
          <Tile label="Next delivery" value={t.next ? ddmmyyyy(t.next) : '—'} tone={t.next ? 'info' : undefined} />
          <Tile label="Past due" value={String(t.overdue)} tone={t.overdue > 0 ? 'warn' : undefined} />
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading && orders.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Loading…</p>}
          {!loading && orders.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-500">
              This vendor has no acknowledged order awaiting delivery.
            </p>
          )}

          <ul className="space-y-2">
            {orders.map((o) => (
              <li key={o.id}>
                <button type="button" onClick={() => open(o.id)}
                        className="group w-full rounded-xl border border-slate-200 p-3 text-left transition
                                   hover:border-teal-300 hover:bg-teal-50/40"
                        title={`Open ${o.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-mono text-xs font-bold text-slate-800">{o.id}</p>
                    <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 group-hover:text-teal-600" />
                  </div>
                  <p className="mt-0.5 truncate text-sm font-semibold text-slate-800">{o.site_name}</p>

                  <div className="mt-1 flex items-baseline justify-between gap-2">
                    <span className="text-[11px] text-slate-500">
                      Ordered {o.ordered_on ? ddmmyyyy(o.ordered_on) : '—'}
                    </span>
                    <span className="text-sm font-bold text-slate-900">{rupees(o.value)}</span>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${
                      o.overdue ? 'bg-amber-50 font-semibold text-amber-800' : 'bg-slate-50 text-slate-600'}`}>
                      <CalendarClock className="h-3 w-3" />
                      {o.deliver_by ? `Deliver by ${ddmmyyyy(o.deliver_by)}` : 'No delivery date'}
                      {o.overdue ? ' · past due' : ''}
                    </span>
                    {o.acknowledged_at && (
                      <span className="rounded-full bg-teal-50 px-2 py-0.5 text-teal-800">
                        Acknowledged {ddmmyyyy(o.acknowledged_at)}
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <footer className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-500">
          Only orders the vendor has acknowledged and not yet delivered. Tap one to open it.
        </footer>
      </aside>
    </>
  )
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'info' | 'warn' }) {
  return (
    <div className={`rounded-lg px-2.5 py-2 ${tone === 'info' ? 'bg-teal-50' : tone === 'warn' ? 'bg-amber-50' : 'bg-slate-50'}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="truncate text-sm font-bold text-slate-900">{value}</p>
      {sub && <p className="text-[10px] text-slate-500">{sub}</p>}
    </div>
  )
}
